import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import useMutation from '../../modules/react/hooks/useMutation';
import useQuery from '../../modules/react/hooks/useQuery';
import BlogMediumField from '../components/BlogMediumField';
import { LimitContextProvider } from '../contexts/LimitContext';

export const UPLOAD_MEDIUM = gql`
  mutation MediumUpload($file: Upload!) {
    mediumUpload(file: $file) {
      medium {
        id
        companyId
        filename
        url
        formattedUpdatedAt
        defaultMetadata {
          title
          altText
        }
      }
      userErrors {
        code
        message
      }
    }
  }
`;

export const GET_MEDIUM = gql`
  query GetMedium($id: ID!) {
    medium(id: $id) {
      id
      ...BlogMediumFieldMedium
    }
  }

  ${BlogMediumField.fragments.medium}
`;

const propTypes = {
  featuredImage: PropTypes.shape({
    id: PropTypes.shape({
      input: PropTypes.shape({
        value: PropTypes.string,
        onChange: PropTypes.func.isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
};
const defaultProps = {};

const BlogMediumFieldContainer = ({
  featuredImage,
  ...rest
}) => {
  const [uploadImage, { loading }] = useMutation(UPLOAD_MEDIUM);
  const { data, loading: queryLoading, refetch } = useQuery(
    GET_MEDIUM, {
      variables: { id: featuredImage.id.input.value },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      skip: !featuredImage.id.input.value,
    },
  );

  const [errors, setErrors] = useState();

  useEffect(() => {
    if (featuredImage.id.input.value) { refetch(); }
  }, [featuredImage.id.input.value]);

  useEffect(() => {
    if (data?.medium) {
      featuredImage.title.input.onChange(data.medium.defaultMetadata.title);
      featuredImage.altText.input.onChange(data.medium.defaultMetadata.altText);
    }
  }, [data?.medium]);

  const onUpload = async ({ target: { files: [file] } }) => {
    if (!file) { return; }
    setErrors();
    const response = await uploadImage({ variables: { file } });

    if (response?.data.mediumUpload.medium) {
      const uploadedMediumId = response.data.mediumUpload.medium.id;
      featuredImage.id.input.onChange(uploadedMediumId);
      featuredImage.title.input.onChange('');
      featuredImage.altText.input.onChange('');
    } else if (response.data.mediumUpload.userErrors) {
      setErrors(response.data.mediumUpload.userErrors);
    }
  };

  return (
    <LimitContextProvider>
      <BlogMediumField
        {...rest}
        onUpload={onUpload}
        errors={errors}
        loading={loading || queryLoading}
        image={data?.medium}
        featuredImage={featuredImage}
      />
    </LimitContextProvider>
  );
};

BlogMediumFieldContainer.propTypes = propTypes;
BlogMediumFieldContainer.defaultProps = defaultProps;

export default BlogMediumFieldContainer;
